import { Component, Inject, OnDestroy, Optional, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AccountsService } from '~/services/administration.ts/accounts';
import { VendorsService } from '~/services/modules/stocks/vendors';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { SharedService } from '~/services/sharedService';
import { LayoutManagerService, PORTAL_DATA } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { PageState } from '~/shared/enums/stock-files.enum';
import { ReportDesignerComponent } from '../../report/designer/reportdesigner.component';
import { ForecastManagerService } from '../forecast-manager';

@Component({
    selector: 'stock-custom-forecast',
    templateUrl: './custom-forecast.html',
    styleUrls: ['./custom-forecast.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class customForecastComponent implements OnDestroy {
  _theme: string = 'enterprise';
  _active: boolean = true;
  listaccounts: any;
  listvendors: any[] = [];
  providedDateValue: Date = null;
  _process: boolean = false;
  _selectedAccount: any[] = [];
  _selectedVendors: any[] = [];
  currentStep = 0;
  reportId: number = 0;
  _invalidForm: boolean;
  _currentForm: FormGroup;
  _steps: Array<{ label: string; optional?: boolean; disabled?: boolean }> = [
    { label: 'Accounts' },
    { label: 'Transaction' },
    { label: 'Forecast Method' },
    { label: 'Variance', optional: true },
    { label: 'Condition', optional: true }
  ];
  editForm: FormGroup;

  constructor(
    private _themeManager: ThemeManagerService,
    private _layoutManager: LayoutManagerService,
    private _accountService: AccountsService,
    private _vendorsService: VendorsService,
    private formBuilder: FormBuilder,
    private _reportService: ReportsService,
    private _forecastManagerService: ForecastManagerService,
    private _layoutComponent: LayoutComponent,
    private _sharedService: SharedService,
    @Optional() @Inject(PORTAL_DATA) public data: any
  ) {
    this._theme = 'enterprise';
    this._themeManager.themeChange.subscribe((theme) => {
      this._theme = theme || 'enterprise';
    });
    this.editForm = this.formBuilder.group({
      forecastName: new FormControl(null, Validators.required),
      overage: new FormControl(),
      replenish: new FormControl(),
      notes: new FormControl(),
      selectedAccount: new FormControl(null, Validators.required),
      selectedVendors: new FormControl([]),
      transaction: new FormControl(null, Validators.required),
      accountForReturn: new FormControl(),
      conditionName: new FormControl(null),
      initialValue: new FormControl(),
      targetedValue: new FormControl(),
      intervalValue: new FormControl(null, Validators.required),
      forecastdate: new FormControl(null, Validators.required),
      provideDate: new FormControl(),
      forecastFor: new FormControl(null, Validators.required),
      providedDate: new FormControl(this.providedDateValue),
      accoutForAllocate: new FormControl(false),
      accoutForMin: new FormControl(false),
      exceedMax: new FormControl(false),
    });

    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });

    this._layoutManager.applyOverly();
    _accountService.getAccountsListing().subscribe((x: any) => {
      let accounts = x?.body?.data || x?.body || x?.data || x || [];
      if (accounts && !Array.isArray(accounts) && accounts.data) {
        accounts = accounts.data;
      }
      this.listaccounts = (Array.isArray(accounts) ? accounts : []).map((a: any) => ({
        id: a.id != null ? a.id : a.accountId,
        name: a.name || a.accountName || a.code || String(a.id)
      }));
    });

    _vendorsService.getVendorsListing().subscribe((x: any) => {
      let vendors = x?.body?.data || x?.body || x?.data || x || [];
      if (vendors && !Array.isArray(vendors) && vendors.data) {
        vendors = vendors.data;
      }
      this.listvendors = (Array.isArray(vendors) ? vendors : []).map((v: any) => ({
        id: v.id != null ? v.id : (v.vendorId != null ? v.vendorId : v.VendorId),
        name: v.name || v.vendorName || v.vendorCode || v.code || String(v.id)
      }));
    });

    const modelData = (this.data && this.data.model) ? this.data.model : (this._sharedService?.model?.model || this._sharedService?.model || this.data);
    if (modelData) {
      this.reportId = modelData.reportId || 0;
      if (this.reportId > 0) {
        this._loadFormByReportId();
      }
    }

    this.editForm.valueChanges.subscribe((x) => {
      this._currentForm = this.getFormByStep(this.currentStep);
      this.validateSteps();
    });
  }

  ngOnDestroy() {
    this._layoutManager.removeOverlay();
    this._layoutComponent.detachDialogForm();
    const closeComponent = {
      component: 'stock-custom-forecast',
      isClosed: true
    };
    this._sharedService.closeDialog.emit(closeComponent);
    this._sharedService.model = {};
  }

  goToStep(step: number): void {
    if (step >= 0 && step < this._steps.length) {
      this.currentStep = step;
      this._invalidForm = false;
    }
  }

  canSave(): boolean {
    const name = this.editForm?.get('forecastName')?.value?.trim();
    return !!name;
  }

  public next() {
    if (this.currentStep < this._steps.length - 1) {
      this.currentStep += 1;
      this._invalidForm = false;
    }
  }


  public prev() {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
  }

  private validateSteps() {
    this._steps.forEach((x) => {
      x['disabled'] = false;
    });
  }

  private getFormByStep(step: number) {
    switch (step) {
      case 0:
        return this.formBuilder.group({
          forecastName: new FormControl(this.editForm.get('forecastName').value, Validators.required),
          selectedAccount: new FormControl(this.editForm.get('selectedAccount').value, Validators.required),

        });
      case 1:
        return this.formBuilder.group({
          transaction: new FormControl(this.editForm.get('transaction').value, Validators.required)
        });
      case 2:
        return this.formBuilder.group({
          intervalValue: new FormControl(this.editForm.get('intervalValue').value, Validators.required),
          forecastdate: new FormControl(this.editForm.get('forecastdate').value, Validators.required),
          forecastFor: new FormControl(this.editForm.get('forecastFor').value, Validators.required),
        });
    }
  }

  _canGoToStep(step: number) {
    return true;
  }

  _loadFormByReportId() {
    this._reportService.ReportById(this.reportId).subscribe(
      (x) => {
        let report = x;
        let config = JSON.parse(report.forecastConfig);
        this.editForm.controls.forecastName.setValue(report.name);
        this.editForm.controls.overage.setValue(config.overage);
        this.editForm.controls.replenish.setValue(config.replenish);
        this.editForm.controls.notes.setValue(config.notes);
        config.selectedAccount.forEach((element: any) => {
          this._selectedAccount.push(element);
        });
        if (config.selectedVendors && Array.isArray(config.selectedVendors)) {
          config.selectedVendors.forEach((element: any) => {
            this._selectedVendors.push(element);
          });
          this.editForm.controls.selectedVendors.setValue(this._selectedVendors);
        }
        this.editForm.controls.transaction.setValue(config.transaction);
        this.editForm.controls.accountForReturn.setValue(config.accountForReturn);
        this.editForm.controls.conditionName.setValue(config.conditionName);
        this.editForm.controls.initialValue.setValue(config.initialValue);
        this.editForm.controls.targetedValue.setValue(config.targetedValue);
        this.editForm.controls.intervalValue.setValue(config.intervalValue);
        this.editForm.controls.forecastdate.setValue(config.forecastdate);
        this.editForm.controls.provideDate.setValue(config.provideDate);
        this.editForm.controls.forecastFor.setValue(config.forecastFor);
        this.providedDateValue = new Date(config.providedDate);
        this.editForm.controls.accoutForAllocate?.setValue(config.accoutForAllocate || false);
        this.editForm.controls.accoutForMin?.setValue(config.accoutForMin || false);
        this.editForm.controls.exceedMax?.setValue(config.exceedMax || false);
      }
    )
  }

  public onProvidedDateChange(value: Date): void {
    this.providedDateValue = value;
  }
  _onStateChanges(e: any) {
    if (e === 'minimized') {
      this._layoutManager.removeOverlay();
    } else {
      this._layoutManager.applyOverly();
    }
  }

  _onCancel() {
    this._closeForm();
  }

  _closeForm() {
    this._active = false;
    this.ngOnDestroy();
  }

 

  SaveAndClose(Process: boolean) {
    // this._active = false;
    this._process = Process;
    this._saveForecast();
  }

  _saveForecast() {
    let reportObject = {
      Name: this.editForm.controls.forecastName.value,
      ForecastConfig: JSON.stringify(
        {
          overage: this.editForm.controls.overage != undefined ? this.editForm.controls.overage.value : 0,
          replenish: this.editForm.controls.replenish != undefined ? this.editForm.controls.replenish.value : 0,
          notes: "",
          selectedAccount: this.editForm.controls.selectedAccount.value,
          selectedVendors: this.editForm.controls.selectedVendors.value || [],
          transaction: this.editForm.controls.transaction != undefined ? this.editForm.controls.transaction.value : "",
          accountForReturn: this.editForm.controls.accountForReturn != undefined ? this.editForm.controls.accountForReturn.value : 0,
          conditionName: this.editForm.controls.conditionName != undefined ? this.editForm.controls.conditionName.value : "",
          initialValue: this.editForm.controls.initialValue != undefined ? this.editForm.controls.initialValue.value : null,
          targetedValue: this.editForm.controls.targetedValue != undefined ? this.editForm.controls.targetedValue.value : null,
          intervalValue: this.editForm.controls.intervalValue != undefined ? this.editForm.controls.intervalValue.value : null,
          forecastdate: this.editForm.controls.forecastdate != undefined ? this.editForm.controls.forecastdate.value : null,
          provideDate: this.editForm.controls.provideDate != undefined ? this.editForm.controls.provideDate.value : null,
          forecastFor: this.editForm.controls.forecastFor != undefined ? this.editForm.controls.forecastFor.value : null,
          providedDate: (this.providedDateValue),
          accoutForAllocate: this.editForm.controls.accoutForAllocate ? this.editForm.controls.accoutForAllocate.value : false,
          accoutForMin: this.editForm.controls.accoutForMin ? this.editForm.controls.accoutForMin.value : false,
          exceedMax: this.editForm.controls.exceedMax ? this.editForm.controls.exceedMax.value : false
        }),
      SystemReport: false,
      IsForecast: true
    }
    this._reportService.saveForecast(this.reportId, reportObject)
      
      .subscribe(
        (x) => {
          this.reportId = x;
          if (this._process == true) {

            this._forecastManagerService.saveCustomForecast(x);
            this._layoutComponent.openDialogForm(ReportDesignerComponent, PageState.EDIT, { reportId: this.reportId });
          }
          this._reportService.refreshReports(x);
          this._active = false;

          this.ngOnDestroy();
        }
      );
  }
}
